/**
 * In-house funnel analytics (W1-9). Fire-and-forget events to
 * POST /public/event — no vendor script, no cookies, no PII.
 *
 * anon_id: random id in localStorage, links a landing visit to an eventual
 * signup. UTM params + referrer are captured once per browser (first touch)
 * and attached to every event, so attribution survives the multi-page
 * signup flow.
 *
 * Events (server allowlist must match): landing_view, cta_click,
 * newsletter_subscribed, signup_complete, essay_view.
 */

const API_URL = process.env.REACT_APP_API_URL;
const PUBLIC_API_URL = API_URL ? API_URL.replace(/\/api$/, '/public') : '';

const ANON_KEY = 'nav_anon_id';
const UTM_KEY = 'nav_first_touch';

function anonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
        b.toString(16).padStart(2, '0')
      ).join('');
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage-' + Math.random().toString(16).slice(2, 10);
  }
}

/** Capture first-touch UTM + referrer once; later visits keep the original. */
function firstTouch() {
  try {
    const existing = localStorage.getItem(UTM_KEY);
    if (existing) return JSON.parse(existing);
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref']) {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 100);
    }
    const touch = {
      utm: Object.keys(utm).length ? utm : null,
      referrer: (document.referrer || '').slice(0, 512) || null,
    };
    localStorage.setItem(UTM_KEY, JSON.stringify(touch));
    return touch;
  } catch {
    return { utm: null, referrer: null };
  }
}

export function track(event, props = {}) {
  if (!PUBLIC_API_URL) return;
  try {
    const touch = firstTouch();
    const body = JSON.stringify({
      anon_id: anonId(),
      event,
      props,
      referrer: touch.referrer,
      utm: touch.utm,
    });
    // sendBeacon survives page navigations (CTA clicks); fetch as fallback.
    const url = `${PUBLIC_API_URL}/event`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // analytics must never break the app
  }
}

/** Fire signup_complete exactly once per browser, on first authenticated load. */
export function trackSignupOnce(userSub) {
  try {
    if (localStorage.getItem('nav_signup_tracked')) return;
    localStorage.setItem('nav_signup_tracked', '1');
    track('signup_complete', userSub ? { user_sub: userSub } : {});
  } catch {
    // ignore
  }
}

/**
 * Authenticated funnel event (POST /event): same shape as track(), but the
 * row carries the user id so post-signup steps join to users. Fire-and-forget.
 */
export async function trackAuthed(event, props = {}) {
  try {
    const { getAuthHeaders } = await import('./api');
    const headers = await getAuthHeaders();
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (!base) return;
    fetch(`${base}/event`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ anon_id: anonId(), event, props }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // analytics must never break the app
  }
}
